import React, { useEffect, useState } from "react";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { getOrgDetails } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import { getEnvironment } from "helpers/environment";
import {
  getSpeedMetrics,
  getTemperatureMetrics,
} from "helpers/metric-settings";
import { UrlParamTypes } from "types/org-display";

const settingsTableStyles = {
  tableCell: (theme: any) => ({
    borderColor: theme.palette.divider,
    borderStyle: "solid",
  }),
  leftCell: {
    borderWidth: "0px 1px 1px 0px",
  },
  rightCell: {
    borderWidth: "0px 0px 1px 0px",
  },
  boldFont: {
    fontWeight: "bold",
  },
};

interface settingsProps {
  key: string;
  title: string;
  value: string;
  testId?: string;
}

interface SettingsTableProps {
  details: settingsProps[];
  orgId: string;
  useOrgMetrics: boolean;
  useOrgDatetimeFormat: boolean;
}

const SettingsTable = ({
  details,
  orgId,
  useOrgMetrics,
  useOrgDatetimeFormat,
}: SettingsTableProps) => {
  const theme = useTheme();
  const { leftCell, rightCell, tableCell, boldFont } = settingsTableStyles;
  const [settings, setSettings] = useState<settingsProps[]>(() => {
    return details;
  });
  const { regionCode } = useParams<UrlParamTypes>();
  const environment = getEnvironment();
  useEffect(() => {
    let settings = details;
    if (useOrgMetrics || useOrgDatetimeFormat) {
      getOrgDetails(
        { roleId: orgId, environment },
        callOptionsHeaders(regionCode, orgId)
      ).then((response) => {
        if (response?.roleDetails?.role?.info === undefined) {
          return;
        }

        const emptyOrgSetting = "Organisation setting";
        if (useOrgMetrics) {
          const { speedMetric, temperatureMetric } =
            response.roleDetails.role.info;
          settings = details.map((setting) => {
            if (setting.key === "speedMetric") {
              setting.value = getSpeedMetrics(speedMetric) || emptyOrgSetting;
            }
            if (setting.key === "temperatureMetric") {
              setting.value =
                getTemperatureMetrics(temperatureMetric) || emptyOrgSetting;
            }
            return setting;
          });
        }

        if (useOrgDatetimeFormat) {
          const { dateFormat, timeFormat } = response.roleDetails.role.info;
          settings = details.map((setting) => {
            if (setting.key === "dateFormat") {
              setting.value = dateFormat || emptyOrgSetting;
            }
            if (setting.key === "timeFormat") {
              setting.value = timeFormat || emptyOrgSetting;
            }
            return setting;
          });
        }

        setSettings(settings);
        return;
      });
      setSettings(settings);
    }
  }, [
    details,
    orgId,
    regionCode,
    environment,
    useOrgMetrics,
    useOrgDatetimeFormat,
  ]);

  return (
    <Box
      style={{
        borderColor: theme.palette.divider,
        borderWidth: "1px",
        borderStyle: "solid",
        borderRadius: "5px",
      }}
    >
      <Table size="small">
        <TableBody>
          {settings.map((item, index) => {
            return (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    ...boldFont,
                    ...tableCell(theme),
                    ...leftCell,
                    wordBreak: "normal",
                  }}
                  align="right"
                >
                  {item.title}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableCell(theme),
                    ...rightCell,
                  }}
                  align="left"
                  data-testid={item.testId}
                >
                  {item.value}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
export default SettingsTable;
